import {
  Home,
  ShoppingCart,
  Target,
  Wallet,
  PieChart,
  ShieldCheck,
  Sparkles,
  ListChecks,
  TrendingUp,
  Calendar,
  BookOpen,
  MessageSquare,
  Settings,
  UserCheck,
  LifeBuoy,
  Megaphone,
  LogOut
} from 'lucide-react';

export const SIDEBAR_HEADER = {
  title: 'FinList Command Center',
  subtitle: 'Plan, track, and celebrate your progress',
  badge: 'Beta'
};

export const SIDEBAR_FOCUS = {
  title: "Today's focus",
  summary: 'Keep your shared grocery plan on budget and celebrate your streak.',
  checklist: [
    'Confirm the essentials on your active list',
    'Log last night\'s purchase to keep totals accurate',
    'Peek at savings progress before the weekend'
  ],
  highlights: [
    {
      id: 'lists-ready',
      label: 'Lists ready',
      value: '2 active',
      accent: 'from-emerald-400/90 to-emerald-500'
    },
    {
      id: 'goal-streak',
      label: 'Goal streak',
      value: '3 weeks',
      accent: 'from-sky-400/90 to-sky-500'
    }
  ]
};

export const SIDEBAR_NAV_GROUPS = [
  {
    id: 'workspace',
    title: 'Workspace',
    caption: 'Your most-used surfaces in one tap',
    items: [
      {
        id: 'dashboard',
        label: 'Mission Control',
        description: 'Balance, sync health, quick actions',
        icon: Home,
        path: '/dashboard',
        badge: 'Live'
      },
      {
        id: 'shopping-lists',
        label: 'Shopping HQ',
        description: 'Lists, delegated runs, and reminders',
        icon: ShoppingCart,
        path: '/shopping-lists'
      },
      // Insights merged into Budget
    ]
  },
  {
    id: 'planning',
    title: 'Planning & Goals',
    caption: 'Stay proactive with upcoming needs',
    items: [
      {
        id: 'tasks',
        label: 'FinTasks',
        description: 'Today’s focus & finance tasks',
        icon: ListChecks,
        path: '/tasks'
      },
      {
        id: 'goals',
        label: 'Savings Goals',
        description: 'Progress, auto-contributions, wins',
        icon: Target,
        path: '/savings-goals'
      },
      {
        id: 'budgets',
        label: 'Budget Dashboard',
        description: 'Track spending, manage envelopes, and stay on target',
        icon: Wallet,
        path: '/budget'
      }
    ]
  },
  {
    id: 'account',
    title: 'Account',
    caption: 'Manage preferences and privacy',
    items: [
      {
        id: 'profile',
        label: 'Profile',
        description: 'Subscription + household access',
        icon: UserCheck,
        path: '/profile'
      },
      {
        id: 'settings',
        label: 'Settings & Privacy',
        description: 'Security, notifications, exports',
        icon: Settings,
        path: '/settings'
      }
    ]
  }
];

export const SIDEBAR_SHORTCUTS = [
  {
    id: 'new-list',
    label: 'Create list',
    description: 'Spin up a shared mission',
    icon: ListChecks,
    path: '/shopping-lists'
  },
  {
    id: 'log-expense',
    label: 'Log purchase',
    description: 'Keep totals accurate',
    icon: Calendar,
    path: '/shopping-list'
  },
  {
    id: 'add-income',
    label: 'Add income',
    description: 'Boost balance & insights',
    icon: TrendingUp,
    path: '/dashboard',
    state: { focus: 'income' }
  },
  {
    id: 'set-goal',
    label: 'Set a goal',
    description: 'Lock in a savings target',
    icon: Target,
    path: '/savings-goals'
  }
];

export const SIDEBAR_SUPPORT = {
  concierge: {
    title: 'Concierge desk',
    description: 'Need a human? We reply fast with practical answers.',
    icon: MessageSquare,
    email: 'support@getfinlist.com',
    responseTime: '<2h avg'
  },
  links: [
    {
      id: 'privacy',
      label: 'Privacy controls',
      description: 'Manage devices, exports, and alerts',
      icon: ShieldCheck,
      path: '/settings'
    },
    {
      id: 'terms',
      label: 'Terms of Service',
      description: 'Our agreement with you',
      icon: BookOpen,
      path: '/terms'
    },
    {
      id: 'privacy-policy',
      label: 'Privacy Policy',
      description: 'How we handle your data',
      icon: ShieldCheck,
      path: '/privacy'
    },
    {
      id: 'premium',
      label: 'Premium benefits',
      description: 'See what\'s included + roadmap',
      icon: Sparkles,
      path: '/subscribe'
    },
    {
      id: 'guides',
      label: 'Playbooks & docs',
      description: 'Deep dives hosted on GitHub',
      icon: BookOpen,
      href: 'https://github.com/walegole/Shopping-List-Git'
    }
  ],
  actions: [
    {
      id: 'status',
      label: 'Status page',
      description: 'Check uptime & incidents',
      icon: LifeBuoy,
      href: 'https://status.finlist.app',
      type: 'link',
      variant: 'success'
    },
    {
      id: 'feedback',
      label: 'Share feedback',
      description: 'Send an idea or bug in 30s',
      icon: Megaphone,
      href: 'https://github.com/walegole/Shopping-List-Git/discussions/new',
      type: 'link',
      variant: 'accent'
    },
    {
      id: 'signout',
      label: 'Sign out',
      description: 'Log out securely on this device',
      icon: LogOut,
      type: 'signout',
      variant: 'danger'
    }
  ]
};

export const SIDEBAR_STATUS = {
  tagline: 'Privacy-first • Offline ready',
  rows: [
    { label: 'Sync health', value: 'Healthy', helper: '0 offline items' },
    { label: 'Encryption', value: 'On-device', helper: 'Secrets never leave your browser' },
    { label: 'Build', value: 'v0.9.2-beta', helper: 'Updated Nov 14, 2025' }
  ]
};
